import { Paper, Box, useMediaQuery } from "@mui/material";
import "../charts.css";
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { getUrl } from "src/Utils/Common";
import { BarChartPinned } from "./BarChartPinned";
import DonutChart from "./DonutChartPinned";
import ValueGrowthIndicator from "./ValueGrowthIndicator";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import DateRangePicker from "src/Components/Charts/ActualVsExpectedMixChart/DatePicker";
import MarketComparisonTableTop5Brands from "src/Components/Tables/MarketComparison/MarketComparisonTop5Brands";

// import { setCurrentChartType } from "src/Redux/chartSlice/chartSlice";

export const PinnedKpis = ({ user, RemoveComponent }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isSmallScreen = useMediaQuery("(max-width:900px)"); 
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:900px)");
  const chartsScrollRef = React.useRef(null);
  const [activeChartIdx, setActiveChartIdx] = useState(0);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priceIndex, setPriceIndex] = useState([]);
  const [valueSalesGrowth, setValueSalesGrowth] = useState([]);
  const [volumeSalesGrowth, setVolumeSalesGrowth] = useState([]);
  const [valueShare, setValueShare] = useState([]);
  const [volumeShare, setVolumeShare] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [timePeriod, setTimePeriod] = useState("P12M");
  const [target, setTarget] = useState(null);
  const [selectedTab, setSelectedTab] = useState(2);

  const [Top5Manufacturer, setTop5Manufacturer] = useState({});
  const [Top5Brands, setTop5Brands] = useState({});

  const [top5Loading, setTop5Loading] = useState(true);

  const projectId = useSelector((state) => state.userProject.projectId);
  // const handleChartChange = (event) => {
  //   const chartType = event.detail;
  //   console.log(`Received chart change event: ${chartType}`);
  //   // You might want to update the chart rendering based on the chartType
  //   // This could involve updating state or re-rendering the component
  // };

  useEffect(() => {
    async function fetchTop5Manufacturer() {
      setTop5Loading(true);
      setError(null);

      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setTop5Loading(false);
        return;
      }

      try {
        let apiUrl = `${getUrl()}/market_overview/top_5_brands_manufacturers?project_id=${projectId}&comparison_type=${timePeriod}&filters={}`;
        if (target !== null && target !== undefined) {
          apiUrl += `&target=${target}`;
        }

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        setTop5Manufacturer(result.Manufacturers || {});
        setTop5Brands(result.Brands || {});

        // Use Manufacturers: index 0 = Marketplace, index 1 = Manufacturer
        const manufacturers = result.Manufacturers;
        if (manufacturers) {
          const value_share = [manufacturers.Value_Share?.[1], manufacturers.Value_Share?.[0]].filter(
            (v) => v !== undefined && v !== null
          );
          const volume_share = [manufacturers.Volume_Share?.[1], manufacturers.Volume_Share?.[0]].filter(
            (v) => v !== undefined && v !== null
          );

          setPriceIndex([
            {
              name: "priceIndex",
              data: [manufacturers.Price_Index_Growth?.[1], manufacturers.Price_Index_Growth?.[0]].filter(
                (v) => v !== undefined && v !== null
              ),
            },
          ]);

          setValueSalesGrowth([
            {
              name: "valueSalesGrowth",
              data: [manufacturers.Value_Sales_Growth?.[1], manufacturers.Value_Sales_Growth?.[0]].filter(
                (v) => v !== undefined && v !== null
              ),
            },
          ]);

          setVolumeSalesGrowth([
            {
              name: "volumeSalesGrowth",
              data: [manufacturers.Volume_Sales_Growth?.[1], manufacturers.Volume_Sales_Growth?.[0]].filter(
                (v) => v !== undefined && v !== null
              ),
            },
          ]);

          setValueShare([
            {
              name: "valueSahre",
              data:
                value_share.length === 2 && value_share[1] === 100
                  ? [value_share[1] - value_share[0], value_share[0]]
                  : value_share,
            },
          ]);

          setVolumeShare([
            {
              name: "VolumeShare",
              data:
                volume_share.length === 2 && volume_share[1] === 100
                  ? [volume_share[1] - volume_share[0], volume_share[0]]
                  : volume_share,
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching Top 5 data:", error);
        setError("Failed to load data");
      } finally {
        setTop5Loading(false);
      }
    }

    fetchTop5Manufacturer();
  }, [projectId, timePeriod, target]);

  useEffect(() => {
    setLoading(top5Loading);
  }, [top5Loading]);

  useEffect(() => {
    const el = chartsScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const firstChild = el.querySelector('[data-chart-item="true"]');
      const pageWidth = (firstChild && firstChild.clientWidth) || el.clientWidth || 1;
      const idx = Math.round(el.scrollLeft / pageWidth);
      setActiveChartIdx(idx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Paper
      sx={{
        width: "100%",
        height: "auto",
        display: "flex",
        flexDirection: "column",
        padding: 0,
        overflow: "hidden",
      }}
      elevation={3}
    >
      <Box sx={{ paddingTop: "16px", paddingBottom: "20px" }}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setTimePeriod={setTimePeriod}
          setTarget={setTarget}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          variant = "default"
        />
      </Box>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowX: isTablet ? "auto" : "visible", 
          overflowY: "hidden",
        }}
        ref={isTablet ? chartsScrollRef : null}
      >
        {/* Charts row */}
        <div
          style={{
            width: "100%",
            padding: "1rem 0",
            overflowX: isMobile ? "auto" : "visible",
          }}
          ref={isMobile ? chartsScrollRef : null}
        >
          <div
            style={{
              display: "flex",
              justifyContent: isSmallScreen ? "flex-start" : "space-between",
              flexDirection: "row",
              flex: "row",
              minWidth: isSmallScreen ? "100%" : "auto",
              paddingLeft: isSmallScreen ? 0 : "1rem",
              scrollSnapType: isSmallScreen ? "x mandatory" : "none",
            }}
          >
          {!top5Loading ? (
            <>
              {valueShare.length > 0 && (
                <div
                  style={{
                    flexShrink: 0,
                    width: isSmallScreen ? "280px" : 250,
                    maxWidth: isSmallScreen ? "280px" : undefined,
                    minWidth: isSmallScreen ? "280px" : undefined,
                    boxSizing: "border-box",
                    scrollSnapAlign: isMobile ? "center" : "none",
                  }}
                  data-chart-item="true"
                >
                  <DonutChart title="Value Share" dataValues={valueShare} />
                </div>
              )}
              {valueSalesGrowth.length > 0 && (
                <div
                  style={{
                    flexShrink: 0,
                    width: isSmallScreen ? "200px" : 190,
                    maxWidth: isSmallScreen ? "200px" : undefined,
                    minWidth: isSmallScreen ? "200px" : undefined,
                    boxSizing: "border-box",
                    scrollSnapAlign: isMobile ? "center" : "none",
                  }}
                  data-chart-item="true"
                >
                  <ValueGrowthIndicator
                    title="Value Sales Growth"
                    dataValues={valueSalesGrowth}
                    displayMode="manufacturer"
                  />
                </div>
              )}
              {volumeShare.length > 0 && (
                <div
                  style={{
                    flexShrink: 0,
                    width: isSmallScreen ? "280px" : 250,
                    maxWidth: isSmallScreen ? "280px" : undefined,
                    minWidth: isSmallScreen ? "280px" : undefined,
                    boxSizing: "border-box",
                    scrollSnapAlign: isMobile ? "center" : "none",
                  }}
                  data-chart-item="true"
                >
                  <DonutChart title="Volume Share" dataValues={volumeShare} />
                </div>
              )}
              {volumeSalesGrowth.length > 0 && (
                <div
                  style={{
                    flexShrink: 0,
                    width: isSmallScreen ? "200px" : 190,
                    maxWidth: isSmallScreen ? "200px" : undefined,
                    minWidth: isSmallScreen ? "200px" : undefined,
                    boxSizing: "border-box",
                    scrollSnapAlign: isMobile ? "center" : "none",
                  }}
                  data-chart-item="true"
                >
                  <ValueGrowthIndicator
                    title="Volume Sales Growth"
                    dataValues={volumeSalesGrowth}
                    displayMode="manufacturer"
                  />
                </div>
              )}
              {priceIndex.length > 0 && (
                <div
                  style={{
                    flexShrink: 0,
                    width: isSmallScreen ? "280px" : 250,
                    maxWidth: isSmallScreen ? "280px" : undefined,
                    minWidth: isSmallScreen ? "280px" : undefined,
                    boxSizing: "border-box",
                    scrollSnapAlign: isMobile ? "center" : "none",
                  }}
                  data-chart-item="true"
                >
                  <BarChartPinned
                    title="Price Index Change"
                    dataValues={priceIndex}
                  />
                </div>
              )}
            </>
          ) : (
            <FacebookCircularProgress />
          )}
          </div>
        </div>
        {isMobile && (
          <div style={{ display: "flex", justifyContent: "center", gap: 6, paddingTop: 8 }}>
            {[
              valueShare.length > 0,
              valueSalesGrowth.length > 0,
              volumeShare.length > 0,
              volumeSalesGrowth.length > 0,
              priceIndex.length > 0,
            ]
              .filter(Boolean)
              .map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: i === activeChartIdx ? "#00265E" : "#D0D5DD",
                    display: "inline-block",
                  }}
                />
              ))}
          </div>
        )}
        {/* Tables row - shares the same horizontal scroll */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            mt: 2,
            pb: { xs: 0, sm: 1 },
            minWidth: { sm: "750px", md: "100%" },
          }}
        >
        {!top5Loading ? (
          <>
            <MarketComparisonTableTop5Brands Top5Manufacturer={Top5Manufacturer} type={"Manufacturer"} timePeriod={timePeriod} />
            <MarketComparisonTableTop5Brands Top5Manufacturer={Top5Brands} type={"Brand"} timePeriod={timePeriod}/>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
              <FacebookCircularProgress sx={{ width: { xs: '100%', sm: 500 }, ml: { xs: 0, sm: 2 } }} />
              <FacebookCircularProgress sx={{ width: { xs: '100%', sm: 500 }, mr: { xs: 0, sm: 2 } }} />
            </Box>
          )}
        </Box>
      </div>
    </Paper>
  );
};
